import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "../../../api/ApiProvider";
import { usePatient } from "../../../stores/Patients/patient.queries";
import { AddressDto, BaseResultDtoKindEnum, PatientDto } from "../../../api-client";

// ============================================================================
// Types
// ============================================================================

export type MatchStatus = "matched" | "partial" | "new" | "loading" | "idle";

export interface PatientAddressMatch {
  status: MatchStatus;
  /** The existing address that matched (if any) */
  matchedAddress: AddressDto | null;
  /** Extracted address string from OCR */
  extractedAddress: string | null;
  /** The full patient record for save operations */
  patient: PatientDto | null;
}

export interface DoctorMatch {
  status: MatchStatus;
  /** Existing doctor data (if found) */
  existingDoctor: any | null;
  /** Fields from the extraction that the existing record is missing */
  missingFields: string[];
  /** Extracted values */
  extracted: { doctorNumber: string | null; doctorName: string | null };
}

export interface ClinicMatch {
  status: MatchStatus;
  /** Existing clinic data (if found) */
  existingClinic: any | null;
  /** Fields from the extraction that the existing record is missing */
  missingFields: string[];
  /** Extracted values */
  extracted: {
    practiceNumber: string | null;
    practiceAddress: string | null;
    practiceContact: string | null;
    doctorSpecialization: string | null;
  };
}

export interface ExtractionMatchResult {
  address: PatientAddressMatch;
  doctor: DoctorMatch;
  clinic: ClinicMatch;
  patientIdentity: PatientIdentityMatch;
}

export type PatientMatchReason = "insurance-number" | "name-dob" | "name-only" | "dob-fuzzy-name";

export interface PatientIdentityMatch {
  status: MatchStatus;
  /** The matched patient (if found by name+DOB) */
  matchedPatientId: number | null;
  matchedFirstName: string | null;
  matchedLastName: string | null;
  matchedDob: string | null;
  /** Why this patient was considered a match */
  matchReason: PatientMatchReason | null;
  /** Extracted values for display / search pre-fill */
  extractedName: string | null;
  extractedDob: string | null;
}

// ============================================================================
// Address matching helpers
// ============================================================================

/** Normalize an address string for comparison (lowercase, collapse whitespace, strip punctuation) */
function normalizeAddr(s: string | null | undefined): string {
  if (!s) return "";
  return s
    .toLowerCase()
    .replace(/[.,\-\/\\]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function addressMatchesExtracted(
  addr: AddressDto,
  extractedAddress: string
): "full" | "partial" | "none" {
  const ci = addr.contactInfo;
  if (!ci) return "none";

  const existing = normalizeAddr(
    [ci.addressLine1, ci.addressLine2, ci.zip, ci.city].filter(Boolean).join(" ")
  );
  const extracted = normalizeAddr(extractedAddress);

  if (!existing || !extracted) return "none";

  // Full match: both strings contain the same address tokens
  if (existing === extracted) return "full";

  // Partial: significant overlap in tokens
  const existingTokens = new Set(existing.split(" ").filter((t) => t.length > 2));
  const extractedTokens = extracted.split(" ").filter((t) => t.length > 2);

  if (extractedTokens.length === 0) return "none";
  const hits = extractedTokens.filter((t) => existingTokens.has(t)).length;
  const ratio = hits / extractedTokens.length;

  if (ratio >= 0.7) return "full";
  if (ratio >= 0.4) return "partial";
  return "none";
}

// ============================================================================
// Doctor / Clinic matching helpers
// ============================================================================

function getMissingDoctorFields(
  existing: any,
  extracted: { doctorName: string | null }
): string[] {
  const missing: string[] = [];
  const ci = existing?.contactInfo;
  if (!ci?.lastName && extracted.doctorName) missing.push("Name");
  return missing;
}

function getMissingClinicFields(
  existing: any,
  extracted: {
    practiceAddress: string | null;
    practiceContact: string | null;
  }
): string[] {
  const missing: string[] = [];
  const ci = existing?.contactInfo;
  if (!ci?.addressLine1 && extracted.practiceAddress) missing.push("Adresse");
  if (!ci?.phone1 && !ci?.fax && extracted.practiceContact) missing.push("Kontakt");
  if (!ci?.businessName) missing.push("Praxisname");
  return missing;
}

// ============================================================================
// String distance helpers
// ============================================================================

/**
 * Levenshtein edit distance between two strings.
 * Used to find patients whose names are misspelt in the system.
 */
export function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;

  // Use single-row optimization
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  let curr = new Array<number>(n + 1);

  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[n];
}

/** Extract the last name from a full OCR name (assumes "FirstName ... LastName") */
function extractLastName(fullName: string | null | undefined): string | null {
  if (!fullName) return null;
  const words = fullName.trim().split(/\s+/);
  return words.length > 0 ? words[words.length - 1] : null;
}

// ============================================================================
// Hook
// ============================================================================

/** Normalize a date string to YYYY-MM-DD for comparison */
function normalizeDateForCompare(dateStr: string | null | undefined): string | null {
  if (!dateStr) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return dateStr.slice(0, 10);
  const match = dateStr.match(/^(\d{2})\.(\d{2})\.(\d{4})/);
  if (match) return `${match[3]}-${match[2]}-${match[1]}`;
  return null;
}

interface UseExtractionMatchingParams {
  patientId: number | undefined;
  extractedAddress: string | null | undefined;
  extractedPatientName: string | null | undefined;
  extractedPatientDob: string | null | undefined;
  extractedInsuranceNo: string | null | undefined;
  doctorNumber: string | null | undefined;
  doctorName: string | null | undefined;
  practiceNumber: string | null | undefined;
  practiceAddress: string | null | undefined;
  practiceContact: string | null | undefined;
  doctorSpecialization: string | null | undefined;
}

export function useExtractionMatching({
  patientId,
  extractedAddress,
  extractedPatientName,
  extractedPatientDob,
  extractedInsuranceNo,
  doctorNumber,
  doctorName,
  practiceNumber,
  practiceAddress,
  practiceContact,
  doctorSpecialization,
}: UseExtractionMatchingParams): ExtractionMatchResult {
  const { doctors: doctorsApi, patients: patientsApi } = useApi();

  // ── Patient data (includes addresses) ──────────────────────
  const { data: patient, isLoading: patientLoading } = usePatient(patientId ?? 0, !!patientId);

  // ── Doctor lookup by LANR ──────────────────────────────────
  const { data: doctorData, isLoading: doctorLoading } = useQuery({
    queryKey: ["extraction-doctor-match", doctorNumber],
    queryFn: async () => {
      try {
        const result = await doctorsApi.doctorControllerGetDoctors(
          doctorNumber!,
          undefined,
          undefined
        );
        // API returns array or single object
        return Array.isArray(result) ? result[0] : result;
      } catch {
        return null;
      }
    },
    enabled: !!doctorNumber && doctorNumber.length >= 3,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // ── Clinic lookup by BSNR ──────────────────────────────────
  const { data: clinicData, isLoading: clinicLoading } = useQuery({
    queryKey: ["extraction-clinic-match", practiceNumber],
    queryFn: async () => {
      try {
        const result = await doctorsApi.doctorControllerGetClinicById(practiceNumber!);
        return result;
      } catch {
        return null;
      }
    },
    enabled: !!practiceNumber && practiceNumber.length >= 3,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // ── Derive match statuses ──────────────────────────────────

  const address = useMemo<PatientAddressMatch>(() => {
    if (!patientId || !extractedAddress) return { status: "idle", matchedAddress: null, extractedAddress: null, patient: patient || null };
    if (patientLoading) return { status: "loading", matchedAddress: null, extractedAddress, patient: null };

    const addresses = patient?.addresses ?? [];
    for (const addr of addresses) {
      const result = addressMatchesExtracted(addr, extractedAddress);
      if (result === "full") {
        return { status: "matched", matchedAddress: addr, extractedAddress, patient: patient! };
      }
    }
    // Check for partial
    for (const addr of addresses) {
      const result = addressMatchesExtracted(addr, extractedAddress);
      if (result === "partial") {
        return { status: "partial", matchedAddress: addr, extractedAddress, patient: patient! };
      }
    }
    return { status: "new", matchedAddress: null, extractedAddress, patient: patient! };
  }, [patient, patientLoading, extractedAddress]);

  const doctor = useMemo<DoctorMatch>(() => {
    if (!doctorNumber) return { status: "idle", existingDoctor: null, missingFields: [], extracted: { doctorNumber: null, doctorName: null } };
    if (doctorLoading) return { status: "loading", existingDoctor: null, missingFields: [], extracted: { doctorNumber, doctorName: doctorName ?? null } };

    if (!doctorData) {
      return { status: "new", existingDoctor: null, missingFields: [], extracted: { doctorNumber, doctorName: doctorName ?? null } };
    }

    const missing = getMissingDoctorFields(doctorData, { doctorName: doctorName ?? null });
    return {
      status: missing.length > 0 ? "partial" : "matched",
      existingDoctor: doctorData,
      missingFields: missing,
      extracted: { doctorNumber, doctorName: doctorName ?? null },
    };
  }, [doctorData, doctorLoading, doctorNumber, doctorName]);

  const clinic = useMemo<ClinicMatch>(() => {
    const clinicExtracted = { practiceNumber: practiceNumber ?? null, practiceAddress: practiceAddress ?? null, practiceContact: practiceContact ?? null, doctorSpecialization: doctorSpecialization ?? null };
    if (!practiceNumber) return { status: "idle", existingClinic: null, missingFields: [], extracted: { ...clinicExtracted, practiceNumber: null } };
    if (clinicLoading) return { status: "loading", existingClinic: null, missingFields: [], extracted: clinicExtracted };

    if (!clinicData) {
      return { status: "new", existingClinic: null, missingFields: [], extracted: clinicExtracted };
    }

    const missing = getMissingClinicFields(clinicData, {
      practiceAddress: practiceAddress ?? null,
      practiceContact: practiceContact ?? null,
    });
    return {
      status: missing.length > 0 ? "partial" : "matched",
      existingClinic: clinicData,
      missingFields: missing,
      extracted: clinicExtracted,
    };
  }, [clinicData, clinicLoading, practiceNumber, practiceAddress, practiceContact, doctorSpecialization]);

  // ── Patient identity lookup by name (only when no patientId yet) ───
  const searchName = useMemo(() => {
    const name = extractedPatientName?.trim() || "";
    if (!name) return "";
    // OCR gives "FirstName LastName"; reverse to "LastName FirstName" for better search
    const words = name.split(/\s+/);
    if (words.length >= 2) {
      return `${words[words.length - 1]} ${words.slice(0, -1).join(" ")}`;
    }
    return name;
  }, [extractedPatientName]);

  const normalizedInsuranceNo = extractedInsuranceNo?.trim() || "";
  const normalizedDob = normalizeDateForCompare(extractedPatientDob) || "";

  // Search by name
  const { data: patientSearchResults, isLoading: patientSearchLoading } = useQuery({
    queryKey: ["extraction-patient-match", searchName],
    queryFn: async () => {
      try {
        const resp = await patientsApi.search(searchName, undefined, undefined, undefined, 20);
        return resp;
      } catch {
        return null;
      }
    },
    enabled: !patientId && searchName.length >= 2,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Search by insurance number (strongest identity signal)
  const { data: insuranceNoResults, isLoading: insuranceNoLoading } = useQuery({
    queryKey: ["extraction-patient-match-insurance", normalizedInsuranceNo],
    queryFn: async () => {
      try {
        return await patientsApi.search(undefined, undefined, normalizedInsuranceNo, undefined, 5);
      } catch {
        return null;
      }
    },
    enabled: !patientId && normalizedInsuranceNo.length >= 3,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Search by DOB (to find patients with misspelt names)
  const { data: dobResults, isLoading: dobLoading } = useQuery({
    queryKey: ["extraction-patient-match-dob", normalizedDob],
    queryFn: async () => {
      try {
        return await patientsApi.search(undefined, undefined, undefined, normalizedDob, 30);
      } catch {
        return null;
      }
    },
    enabled: !patientId && normalizedDob.length === 10,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const patientIdentity = useMemo<PatientIdentityMatch>(() => {
    const extracted = { extractedName: extractedPatientName ?? null, extractedDob: extractedPatientDob ?? null };
    const nullMatch: PatientIdentityMatch = { status: "idle", matchedPatientId: null, matchedFirstName: null, matchedLastName: null, matchedDob: null, matchReason: null, ...extracted };

    // If patient already assigned, always show matched
    if (patientId) {
      const searchHit = patientSearchResults?.results?.find(
        (r) => r.patientId === patientId
      );
      return {
        status: "matched",
        matchedPatientId: patientId,
        matchedFirstName: patient?.firstName ?? searchHit?.firstName ?? null,
        matchedLastName: patient?.lastName ?? searchHit?.lastName ?? null,
        matchedDob: patient?.dob ?? searchHit?.dob ?? null,
        matchReason: null,
        ...extracted,
      };
    }

    const anyLoading = patientSearchLoading || insuranceNoLoading || dobLoading;

    // Priority 1: Insurance number exact match (strongest signal)
    const insurancePatients = insuranceNoResults?.results?.filter(
      (r) => r.kind === BaseResultDtoKindEnum.Patient && r.patientId != null
    ) ?? [];
    if (insurancePatients.length === 1) {
      return {
        status: "matched",
        matchedPatientId: insurancePatients[0].patientId!,
        matchedFirstName: insurancePatients[0].firstName ?? null,
        matchedLastName: insurancePatients[0].lastName ?? null,
        matchedDob: insurancePatients[0].dob ?? null,
        matchReason: "insurance-number",
        ...extracted,
      };
    }
    if (insurancePatients.length > 1) {
      return { ...nullMatch, status: "partial", matchReason: "insurance-number", ...extracted };
    }

    // Priority 2: Name search + DOB match
    if (!searchName || searchName.length < 2) {
      if (anyLoading) return { ...nullMatch, status: "loading", ...extracted };
      return { ...nullMatch, status: normalizedDob ? "loading" : "idle", ...extracted };
    }

    if (anyLoading) {
      return { ...nullMatch, status: "loading", ...extracted };
    }

    const namePatients = patientSearchResults?.results?.filter(
      (r) => r.kind === BaseResultDtoKindEnum.Patient && r.patientId != null
    ) ?? [];

    const normalizedExtractedDob = normalizeDateForCompare(extractedPatientDob);
    if (normalizedExtractedDob) {
      const dobMatch = namePatients.find((p) => {
        const candidateDob = p.dob ? p.dob.slice(0, 10) : null;
        return candidateDob === normalizedExtractedDob;
      });
      if (dobMatch) {
        return {
          status: "matched",
          matchedPatientId: dobMatch.patientId!,
          matchedFirstName: dobMatch.firstName ?? null,
          matchedLastName: dobMatch.lastName ?? null,
          matchedDob: dobMatch.dob ?? null,
          matchReason: "name-dob",
          ...extracted,
        };
      }
    }

    // Priority 3: DOB match + fuzzy last name (catches misspellings)
    if (normalizedExtractedDob) {
      const dobPatients = dobResults?.results?.filter(
        (r) => r.kind === BaseResultDtoKindEnum.Patient && r.patientId != null
      ) ?? [];

      const extractedLastName = extractLastName(extractedPatientName);
      if (extractedLastName && dobPatients.length > 0) {
        const fuzzyMatch = dobPatients.find((p) => {
          if (!p.lastName) return false;
          const distance = levenshtein(
            p.lastName.toLowerCase(),
            extractedLastName.toLowerCase()
          );
          const maxLen = Math.max(p.lastName.length, extractedLastName.length);
          return maxLen > 0 && distance / maxLen <= 0.35;
        });
        if (fuzzyMatch) {
          return {
            status: "partial",
            matchedPatientId: fuzzyMatch.patientId!,
            matchedFirstName: fuzzyMatch.firstName ?? null,
            matchedLastName: fuzzyMatch.lastName ?? null,
            matchedDob: fuzzyMatch.dob ?? null,
            matchReason: "dob-fuzzy-name",
            ...extracted,
          };
        }
      }
    }

    // Found name candidates but no DOB match → partial (manual selection needed)
    if (namePatients.length > 0) {
      return { ...nullMatch, status: "partial", matchReason: "name-only", ...extracted };
    }

    // No matches at all → new patient
    return { ...nullMatch, status: "new", ...extracted };
  }, [patientId, patient, patientLoading, searchName, patientSearchLoading, patientSearchResults, insuranceNoResults, insuranceNoLoading, dobResults, dobLoading, extractedPatientName, extractedPatientDob, normalizedInsuranceNo, normalizedDob]);

  return { address, doctor, clinic, patientIdentity };
}
